import {ChevronDownIcon, ChevronRightIcon, FileSymlinkFileIcon} from '@primer/octicons-react'
import {IconButton, Label, Link, Heading} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import styles from './ConversationHeader.module.css'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'

type ConversationHeaderProps = {
  accessibleContextLabel?: string
  firstCommentId?: number | null
  href?: string
  isCollapsed: boolean
  isOutdated: boolean
  isResolved: boolean
  startLine?: number | null
  line?: number | null
  onNavigateToDiffComment: () => void
  onToggleCollapsed: () => void
  path: string
  resolvedLabel?: string
  rightSideContent?: React.ReactElement | null
  threadId: string
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export function ConversationHeader({
  accessibleContextLabel,
  firstCommentId,
  href,
  isCollapsed: initialIsCollapsed,
  isOutdated,
  isResolved,
  startLine,
  line,
  onToggleCollapsed,
  onNavigateToDiffComment,
  path,
  resolvedLabel,
  rightSideContent,
  threadId,
  headingLevel = 'h3',
}: ConversationHeaderProps) {
  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(threadId, initialIsCollapsed)

  const lineText = startLine ? `Lines ${startLine} - ${line}` : `Line ${line}`
  const handleToggleCollapsed = () => {
    setIsCollapsed(prevIsCollapsed => !prevIsCollapsed)
    onToggleCollapsed()
  }
  const commentHref = href ?? `#r${firstCommentId}`
  const collapseLabel = isCollapsed ? 'Open review comment' : 'Close review comment'

  return (
    <div className="d-flex flex-row flex-items-center">
      <IconButton
        aria-label={accessibleContextLabel ? `${collapseLabel} in ${accessibleContextLabel}` : collapseLabel}
        description={accessibleContextLabel ? collapseLabel : undefined}
        icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        size="small"
        variant="invisible"
        onClick={handleToggleCollapsed}
        className="ml-n1"
      />
      <Heading as={headingLevel} className="d-flex flex-items-center flex-1 min-width-0 mr-2 ml-1 f6 text-semibold">
        <Tooltip direction="n" text={path} type="label">
          <Link
            className={clsx(
              styles['file-name-overflow'],
              'd-inline text-mono no-wrap overflow-hidden direction-rtl fgColor-default',
            )}
            onClick={onNavigateToDiffComment}
            href={commentHref}
            muted
          >
            {/* Ensures path is displayed in left-to-right-order despite container direction being right-to-left */}
            &lrm;{path}&lrm;
          </Link>
        </Tooltip>
        {!!line && <span className="f6 fgColor-muted text-normal no-wrap ml-2">{lineText}</span>}
      </Heading>
      {isResolved && (
        <Label size="large" className="mx-1" variant="secondary">
          {resolvedLabel ?? 'Resolved'}
        </Label>
      )}
      {isOutdated && !isResolved && (
        <Label size="large" className="mx-1" variant="attention">
          Outdated
        </Label>
      )}
      <IconButton
        as="a"
        aria-label={
          accessibleContextLabel
            ? `Jump to the comment in the diff for ${accessibleContextLabel}`
            : 'Jump to the comment in the diff'
        }
        description={accessibleContextLabel ? 'Jump to the comment in the diff' : undefined}
        tooltipDirection="se"
        icon={FileSymlinkFileIcon}
        variant="invisible"
        href={commentHref}
        onClick={onNavigateToDiffComment}
      />
      {rightSideContent}
    </div>
  )
}
